// src/firebase.ts
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  onMessage,
  Messaging,
  MessagePayload,
  getToken,
} from "firebase/messaging";
import axiosInstance from "./api/axios";
import toastService from "./services/toastService";
import { shouldInitializeFirebase } from "./utils/firebaseUtils";
// import axiosInstance from "./api/axios";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_API_KEY,
  authDomain: import.meta.env.VITE_APP_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_APP_ID,
  measurementId: import.meta.env.VITE_APP_MEASUREMENT_ID,
};

// Conditionally initialize Firebase
let app: any = null;
let messaging: Messaging | null = null;

if (shouldInitializeFirebase()) {
  try {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } catch (error) {
    console.error('Firebase initialization failed:', error);
  }
}

async function detectIncognito(): Promise<boolean | null> {
  try {
    // Method 1: Check multiple incognito indicators
    const tests = {
      // Safari
      // safariPersistentStorage: 'webkitPersistentStorage' in navigator 
      //   ? navigator.webkitPersistentStorage?.queryUsageAndQuota === undefined 
      //   : false,
      
      // Firefox
      firefoxServiceWorker: 'serviceWorker' in navigator 
        ? await navigator.serviceWorker.getRegistrations().then(regs => regs.length === 0) 
        : false,
      
      // Chrome/Edge storage quota
      storageQuota: 'storage' in navigator && 'estimate' in navigator.storage 
        ? await navigator.storage.estimate().then(e => (e.quota || 0) < 12000000) 
        : false,
      
      // FileSystem API
      fileSystem: !(window as any).RequestFileSystem && !(window as any).webkitRequestFileSystem,
      
      // LocalStorage test
      localStorageTest: (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return false;
        } catch {
          return true;
        }
      })()
    };

    // If any test returns true, we're likely in incognito
    const isIncognito = Object.values(tests).some(test => test === true);
    
    // Additional verification for false positives
    if (!isIncognito) {
      // Check if we got a false negative by verifying multiple methods agree
      const positiveTests = Object.values(tests).filter(test => test === true).length;
      const negativeTests = Object.values(tests).filter(test => test === false).length;
      
      // If most indicators suggest incognito, trust them
      return positiveTests > negativeTests ? true : false;
    }

    return isIncognito;
  } catch (error) {
    console.error('Incognito detection error:', error);
    return null;
  }
}


export const saveToken = async () => {
  if (!shouldInitializeFirebase()) {
    console.log('Skipping saveToken - Firebase not initialized');
    return;
  }
  
  try {
    // setLoading(true);
    const accessToken = localStorage.getItem("accessToken");
    const token = sessionStorage.getItem("notificationToken");
    if (accessToken && token) {
      const response = await axiosInstance({
        method: "POST",
        url: "/user/api/v1/deviceToken/push",
        data: {
          token: token,
          platform: 3,
        },
      });
      console.log("save token response", response);
    }
  } catch (e) {
    console.log(e);
  }
};

// Function to request permission and get FCM token
export const requestForToken = async (): Promise<string | undefined> => {
  if (!shouldInitializeFirebase() || !messaging) {
    console.log('Skipping requestForToken - Firebase not initialized');
    return undefined;
  }
  
  try {
    const isCognitoMode = await detectIncognito();
    console.log(isCognitoMode,"isCognitoMode")
    const permission = await Notification.requestPermission();
    console.log("notification requested", permission);
    if (permission === "granted") {
      const currentToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_APP_VAPID_KEY,
      });
      if (currentToken) {
        console.log("FCM Token:", currentToken);
        sessionStorage.setItem("notificationToken", currentToken);
        await saveToken();
        return currentToken;
      } else {
        console.log(
          "No registration token available. Request permission to generate one."
        );
        return undefined;
      }
    } else if (permission === "denied" && isCognitoMode ===false) {
      // alert("You denied for the notification");
      if (!sessionStorage.getItem("notificationToastShown")) {
        toastService.success("You denied for the notification");
        sessionStorage.setItem("notificationToastShown", "true");
      }
    }
  } catch (error) {
    console.error("An error occurred while retrieving token. ", error);
  }
};

// Function to handle incoming messages
// firebase.ts
export const onMessageListener = (callback: (payload: MessagePayload) => void) => {
    if (!shouldInitializeFirebase() || !messaging) {
    console.log('Skipping onMessageListener - Firebase not initialized');
    return Promise.resolve({} as MessagePayload);
  }
  onMessage(messaging, callback);
};

// export const onMessageListener = (): Promise<MessagePayload> => {
//   if (!shouldInitializeFirebase() || !messaging) {
//     console.log('Skipping onMessageListener - Firebase not initialized');
//     return Promise.resolve({} as MessagePayload);
//   }
  
//   return new Promise((resolve) => {
//     onMessage(messaging!, (payload) => {
//       resolve(payload);
//     });
//   });
// };
